import React, { useCallback, useState } from "react";
import { HStack } from "@design-system/HStack/HStack";
import { FinancialConnectionWithAccounts } from "@common/types/financialConnection";
import { useUser } from "@hooks/useUser";
import { useFinancialAccountEntitySelection } from "@hooks/useFinancialAccountEntitySelection";
import { Button } from "@design-system/Button/Button";
import {
    StandardModal,
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { FinancialAccountsConnectionProvider } from "./FinancialAccountsConnectionProvider";
import { FinancialAccountsConnectionWidget } from "./FinancialAccountConnectionWidget";
import { isValidConnection } from "./helpers";

interface FinancialAccountsConnectedModalProps {
    show: boolean;
    connection: FinancialConnectionWithAccounts;
    onHide(): void;
}

export const FinancialAccountsConnectedModal: React.FC<
    FinancialAccountsConnectedModalProps
> = ({ show, connection, onHide }) => {
    const { showBusinessEntitySelection } =
        useFinancialAccountEntitySelection();
    const [submitted, setSubmitted] = useState<boolean>(false);
    const user = useUser();
    const handleSubmit = useCallback(() => {
        if (
            isValidConnection(connection) ||
            submitted ||
            !user.onboardingComplete
        ) {
            setSubmitted(false);
            onHide();
        } else {
            setSubmitted(true);
        }
    }, [connection, onHide, submitted, user.onboardingComplete]);

    return (
        <FinancialAccountsConnectionProvider
            showAccountTypeWarning={submitted && user.onboardingComplete}
        >
            <StandardModal
                show={show}
                onHide={onHide}
                size={connection && showBusinessEntitySelection ? "xl" : "lg"}
                backdrop={connection ? true : "static"}
                dialogClassName={
                    showBusinessEntitySelection
                        ? "accounts-connected-modal__dialog--with-entities"
                        : "accounts-connected-modal__dialog"
                }
            >
                <>
                    <StandardModalHeader>Select entity</StandardModalHeader>
                    <StandardModalBody>
                        <p>For which entity are you adding these accounts?</p>

                        <FinancialAccountsConnectionWidget
                            accounts={connection.accounts}
                            connection={connection}
                        />

                        <HStack as="footer" align="center" justify="end">
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                size="xl"
                            >
                                Continue
                            </Button>
                        </HStack>
                    </StandardModalBody>
                </>
            </StandardModal>
        </FinancialAccountsConnectionProvider>
    );
};
